import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { isOptionEqualToValue } from '../utils';
import {
  useGetBookingOriginsQuery,
  useGetLocationsQuery,
  useGetShippersQuery,
} from '../batchesEndpoints';

const Shipment = ({
  batchState,
  index,
  setShipmentState,
  shipment,
  validationState,
  setValidationState,
}) => {
  const { batchOwner, origin, product } = batchState;
  const {
    agreementCode,
    shipper,
    bookingOrigin,
    location,
    defaultShipperLocation,
    volume,
    comment,
  } = shipment;

  const { dataCompanyId } = batchOwner;
  const inventoryOwner = { companyCustomerIdName: batchOwner.customerIdName, dataCompanyId };
  const [defaultAutoShipperLocation, setDefaultAutoShipperLocation] =
    React.useState(defaultShipperLocation);

  const shipperIsBatchOwner = R.compose(
    R.whereEq({
      shipperIdName: batchOwner.customerIdName,
      shipperName: batchOwner.customerNameShort,
    }),
    R.defaultTo({})
  )(shipper);

  const intialBookingOrigin = {
    bookingOriginId: origin.originId,
    bookingOriginName: origin.originName,
  };

  const {
    data: shippersQuery = [],
    isFetching: isFetchingShippers,
    isSuccess: isShippersSuccess,
  } = useGetShippersQuery({ params: { dataCompanyId } });

  const {
    data: bookingOriginsQuery = [],
    isFetching: isFetchingBookingOrigins,
    isSuccess: isBookingOriginsSuccess,
  } = useGetBookingOriginsQuery({ params: { inventoryOwner, originId: origin.originId } });

  const {
    data: locationsQuery = [],
    isFetching: isFetchingLocations,
    isSuccess: isLocationsSuccess,
  } = useGetLocationsQuery(
    {
      params: {
        bookingOriginId: R.prop('bookingOriginId', bookingOrigin),
        dataCompanyId,
        inventoryOwner,
        productId: R.prop('productId', product),
      },
    },
    { skip: R.isNil(bookingOrigin) }
  );

  const handleShipperChange = (_, value) => {
    setShipmentState(R.mergeLeft({ location: null, shipper: value }));
  };

  const handleBookingOriginChange = (_, value) => {
    setShipmentState(R.pipe(R.assoc('bookingOrigin', value), R.assoc('location', null)));
    setShipmentState(R.assoc('defaultShipperLocation', R.prop('defaultLocationName', value)));
    setDefaultAutoShipperLocation(R.prop('defaultLocationName', value));
  };

  const handleLocationChange = (_, value) => {
    setShipmentState(R.assoc('location', value));
  };

  const validateVolume = R.compose(
    setValidationState,
    R.assoc('volume'),
    R.cond([
      [R.isEmpty(R.__), R.always('Volume must be greater than or equal to 100.')],
      [R.lt(R.__, 100), R.always('Volume must be greater than or equal to 100.')],
      [R.T, R.always('')],
    ])
  );

  const handleVolumeChange = R.compose(
    setShipmentState,
    R.assoc('volume'),
    x => x.toLocaleString('en-US'),
    R.unless(R.isEmpty, parseInt),
    R.tap(validateVolume),
    R.replace(/\D/g, ''),
    R.path(['target', 'value'])
  );

  const handleAgreementCodeChange = R.compose(
    setShipmentState,
    R.assoc('agreementCode'),
    R.path(['target', 'value'])
  );

  const validateComment = R.compose(
    setValidationState,
    R.assoc('comment'),
    R.ifElse(R.gte(255), R.always(''), R.always('Cannot exceed 255 characters')),
    R.length
  );

  const handleCommentChange = R.compose(
    setShipmentState,
    R.assoc('comment'),
    R.tap(validateComment),
    R.path(['target', 'value'])
  );

  useEffect(() => {
    const fetchedBookingOrigin = R.find(
      R.propEq('bookingOriginId', R.prop('bookingOriginId', intialBookingOrigin)),
      bookingOriginsQuery
    );
    if (!R.isNil(fetchedBookingOrigin) && R.isNil(bookingOrigin)) {
      setShipmentState(R.assoc('bookingOrigin', fetchedBookingOrigin));
    } else if (R.isNil(bookingOrigin)) {
      setShipmentState(R.assoc('bookingOrigin', intialBookingOrigin));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingOriginsQuery]);

  useEffect(() => {
    if (R.isNil(location)) {
      // hidden is set to true to prevent location from being displayed first time, at subsequent changes it will be displayed
      R.compose(R.unless(R.isNil, x => handleLocationChange('_', { hidden: true, ...x })))(
        locationsQuery
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, locationsQuery]);

  useEffect(() => {
    if (R.isNil(defaultShipperLocation) && R.isNil(defaultAutoShipperLocation)) {
      const defaultLocation = R.find(R.propEq('isDefaultLocation', true), locationsQuery);
      setShipmentState(R.assoc('defaultShipperLocation', R.prop('locationName', defaultLocation)));
      setDefaultAutoShipperLocation(R.prop('locationName', defaultLocation));
    }
  }, [locationsQuery, defaultAutoShipperLocation, defaultShipperLocation, setShipmentState]);
  useEffect(() => {
    if (R.isNil(shipper) && index === 0) {
      R.compose(
        R.unless(R.isNil, x => handleShipperChange('_', x)),
        R.find(s => R.equals(s.shipperIdName, batchOwner.customerIdName))
      )(shippersQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipper, shippersQuery, index]);

  const locationHelperText = R.propEq('isDefaultLocation', false, location)
    ? 'This will create a linked distribution going to the selected location for the shipment.'
    : 'Optional, select a location to create a distribution for your shipment';

  return (
    <Stack spacing={2} sx={{ mt: 0, py: 0 }}>
      <Autocomplete
        disableClearable
        disabled={!isShippersSuccess}
        getOptionLabel={R.prop('shipperName')}
        isOptionEqualToValue={isOptionEqualToValue('shipperIdName')}
        loading={isFetchingShippers}
        onChange={handleShipperChange}
        options={shippersQuery}
        renderInput={inputParams => <TextField {...inputParams} label="Shipper" />}
        value={shipper}
      />
      <Autocomplete
        disableClearable
        disabled={R.or(!isBookingOriginsSuccess, bookingOriginsQuery.length === 1)}
        getOptionLabel={R.prop('bookingOriginName')}
        isOptionEqualToValue={isOptionEqualToValue('bookingOriginId')}
        loading={isFetchingBookingOrigins}
        onChange={handleBookingOriginChange}
        options={bookingOriginsQuery}
        renderInput={inputParams => <TextField {...inputParams} label="Booking Origin" />}
        value={bookingOrigin}
      />
      <Stack
        sx={{
          alignItems: 'flex-start',
          display: 'inline-flex',
          gap: 0.5,
          justifyContent: 'flex-start',
        }}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          <Typography variant="caption">Default Auto-Distribution Location </Typography>
          <Tooltip
            arrow
            enterTouchDelay={10}
            placement="top"
            title={
              <span
                style={{
                  display: 'block',
                  fontSize: '13px',
                  padding: '3px',
                  wordWrap: 'break-word',
                }}
              >
                If you do not create a distribution for this shipment, all of the barrels will
                auto-distribute to this defined location.
              </span>
            }
          >
            <InfoRoundedIcon fontSize="small" sx={{ color: 'graphic.main' }} />
          </Tooltip>
        </Stack>
        <Typography sx={{ fontSize: '16px', overflowWrap: 'anywhere' }} variant="subtitle2">
          {defaultAutoShipperLocation ?? 'No Default Location'}
        </Typography>
      </Stack>
      <Stack spacing={1}>
        <Autocomplete
          disabled={!isLocationsSuccess || !shipperIsBatchOwner || R.isEmpty(locationsQuery)}
          getOptionLabel={R.prop('locationName')}
          isOptionEqualToValue={isOptionEqualToValue('id')}
          loading={isFetchingLocations}
          onChange={handleLocationChange}
          options={locationsQuery}
          renderInput={inputParams => (
            <TextField
              {...inputParams}
              helperText={locationHelperText}
              label="Distribution Location"
            />
          )}
          sx={{
            '& .MuiAutocomplete-clearIndicator': {
              '&:focus-visible': {
                outline: '2px solid #1976d2',
                outlineOffset: 2,
              },
              '&:hover': { background: '#2b2f34' },
              // Hide dropdown caret when clear button is present
              '+ .MuiAutocomplete-popupIndicator': {
                display: 'none',
              },
              alignItems: 'center',
              background: '#3C4148',
              borderRadius: '50%',
              color: '#fff',
              display: 'flex',
              height: 25,
              justifyContent: 'center',
              justifySelf: 'center',
              mr: '2px',
              p: 0,
              width: 25,
            },
            '& .MuiAutocomplete-clearIndicator svg': {
              color: '#fff',
              fontSize: 17,
            },
          }}
          value={!R.prop('hidden', location) ? location : null}
        />
        {R.and(isLocationsSuccess, R.isEmpty(locationsQuery)) && (
          <Alert severity="warning">
            There are no valid locations available for selection. Please select a different Booking
            Origin or return to step 1 and select a different origin or product.
          </Alert>
        )}
        {R.all(R.equals(true), [
          R.not(R.isEmpty(locationsQuery)),
          R.isNil(R.find(R.prop('isDefaultLocation'))(locationsQuery)),
          R.not(R.isNil(shipper)),
          R.not(shipperIsBatchOwner),
        ]) && (
          <Alert severity="warning">
            A shipment cannot be made for your selected Shipper. Please select yourself as the
            Shipper or a different Booking Origin, or return to step 1 and select a different origin
            or product.
          </Alert>
        )}
      </Stack>
      <TextField
        error={!R.isEmpty(validationState.volume)}
        fullWidth
        helperText={validationState.volume}
        label="Volume"
        onChange={handleVolumeChange}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
          },
        }}
        value={volume}
      />
      <TextField
        fullWidth
        helperText="Optional"
        label="Agreement Code"
        onChange={handleAgreementCodeChange}
        value={agreementCode}
      />
      <TextField
        error={!R.isEmpty(validationState.comment)}
        helperText={validationState.comment || 'Optional'}
        label="Comment"
        multiline
        onChange={handleCommentChange}
        rows={3}
        value={comment}
      />
    </Stack>
  );
};

Shipment.propTypes = {
  batchState: PropTypes.shape({
    batchOwner: PropTypes.shape({
      customerIdName: PropTypes.string,
      customerNameShort: PropTypes.string,
      dataCompanyId: PropTypes.number,
    }),
    origin: PropTypes.shape({
      originId: PropTypes.number,
      originName: PropTypes.string,
    }),
    product: PropTypes.shape({}),
  }).isRequired,
  index: PropTypes.number.isRequired,
  setShipmentState: PropTypes.func.isRequired,
  setValidationState: PropTypes.func.isRequired,
  shipment: PropTypes.shape({
    agreementCode: PropTypes.string,
    bookingOrigin: PropTypes.shape({
      defaultLocation: PropTypes.number,
      defaultLocationName: PropTypes.string,
    }),
    comment: PropTypes.string,
    defaultShipperLocation: PropTypes.string,
    location: PropTypes.shape({}),
    shipper: PropTypes.shape({}),
    volume: PropTypes.string,
  }).isRequired,
  validationState: PropTypes.shape({
    comment: PropTypes.string,
    volume: PropTypes.string,
  }).isRequired,
};

export default Shipment;
